import { z } from 'zod';

/**
 * What the core shows for an app's page (`page` in `crowdex.json`) besides its view: the items its
 * sidebar route lists, and the remote web app a `url` page opens.
 */

/** One entry of a page's sidebar, as its `sidebar` route returns it. */
export const sidebarItemSchema = z
  .object({
    id: z.string().min(1).max(256),
    title: z.string().min(1).max(200),
    /** A second line under the title (a status, a date). */
    detail: z.string().max(200).optional(),
    /** An icon name, or an image URL. */
    icon: z.string().min(1).max(160).optional(),
    /** Items of a group are listed under it, as conversations are under their project. */
    group: z.string().min(1).max(80).optional(),
    /**
     * When the item last changed (milliseconds since the epoch): the Recent sort, and the age
     * shown when there is no `detail`.
     */
    updatedAt: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SidebarItem = z.infer<typeof sidebarItemSchema>;

/** What the route of a page's sidebar (`page.sidebars[].route`) returns. */
export const sidebarItemsSchema = z
  .object({ items: z.array(sidebarItemSchema).max(1000) })
  .strict();
export type SidebarItems = z.infer<typeof sidebarItemsSchema>;

/**
 * A live section of a view's navigation (`page.sections.set`): what the document open in the
 * view has (a design's pages, a deck's slides), listed by the core in the sidebar above the app's
 * sections while its tab is shown. `selected` is the item the view shows.
 */
export const pageSectionSchema = z
  .object({
    id: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/, 'Section ids are short names.'),
    title: z.string().min(1).max(80),
    items: z.array(sidebarItemSchema).max(500),
    selected: z.string().max(256).nullable().optional(),
  })
  .strict();
export type PageSection = z.infer<typeof pageSectionSchema>;

/** What `page.sections.set` takes: every live section of the view (none clears them). */
export const pageSectionsSchema = z
  .object({ sections: z.array(pageSectionSchema).max(4) })
  .strict()
  .refine(
    ({ sections }) => new Set(sections.map((section) => section.id)).size === sections.length,
    'Each section has its own id.',
  );
export type PageSections = z.infer<typeof pageSectionsSchema>;

/**
 * An action of the host's bar over a view (`page.header.set`): a button, or a menu of choices when
 * it has `menu`. A click reaches the view as `context.action` (the id of the action, or of the
 * choice of its menu). One action at most is `primary`.
 */
const headerActionIdSchema = z
  .string()
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,63}$/, 'Action ids are short names.');
export const pageHeaderActionSchema = z
  .object({
    id: headerActionIdSchema,
    label: z.string().min(1).max(60),
    /** An icon name (`plus`, `play`, `share`), as `page.sidebars` items name theirs. */
    icon: z.string().min(1).max(64).optional(),
    primary: z.boolean().optional(),
    /** The keys the host shows beside it (`⌘⏎`); the view answers them itself. */
    shortcut: z.string().min(1).max(24).optional(),
    disabled: z.boolean().optional(),
    menu: z
      .array(
        z
          .object({
            id: headerActionIdSchema,
            label: z.string().min(1).max(60),
            icon: z.string().min(1).max(64).optional(),
            disabled: z.boolean().optional(),
          })
          .strict(),
      )
      .max(20)
      .optional(),
  })
  .strict();
export type PageHeaderAction = z.infer<typeof pageHeaderActionSchema>;

/**
 * What `page.header.set` takes: the title of what the view shows (the item's), its actions and a
 * short status ("Saved", "Running"). The host draws them in its bar; a view never draws a header.
 */
export const pageHeaderSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    actions: z.array(pageHeaderActionSchema).max(8).default([]),
    status: z.string().min(1).max(60).optional(),
  })
  .strict()
  .refine(
    ({ actions }) => actions.filter((action) => action.primary === true).length <= 1,
    'One action at most is primary.',
  )
  .refine(({ actions }) => {
    const ids = actions.flatMap((action) => [
      action.id,
      ...(action.menu ?? []).map((choice) => choice.id),
    ]);
    return new Set(ids).size === ids.length;
  }, 'Each action has its own id.');
export type PageHeader = z.output<typeof pageHeaderSchema>;
export type PageHeaderInput = z.input<typeof pageHeaderSchema>;

/**
 * What `page.navigate` takes: a route of the view (`/runs/42`), which the host puts in the address
 * (`/apps/<plugin>/runs/42`); `push: false` replaces the current level instead of adding one.
 */
const viewRouteSchema = z
  .string()
  .startsWith('/')
  .max(512)
  .refine((route) => !route.split('/').includes('..'), 'A route stays in the view.');
export const pageNavigateSchema = z
  .object({ route: viewRouteSchema, push: z.boolean().optional() })
  .strict();
export type PageNavigate = z.infer<typeof pageNavigateSchema>;

/**
 * What `page.inspector.open` takes: a route of the view, which the host shows in its inspector
 * (a second document of the view, 320 px at the right of the frame, `context.layout`
 * `inspector`), and the inspector's title. Opening it again with another route replaces it.
 */
export const pageInspectorSchema = z
  .object({ route: viewRouteSchema, title: z.string().min(1).max(80).optional() })
  .strict();
export type PageInspector = z.infer<typeof pageInspectorSchema>;

const loopbackHostname = (hostname: string) =>
  hostname === 'localhost' ||
  hostname.endsWith('.localhost') ||
  hostname === '127.0.0.1' ||
  hostname === '[::1]';

/**
 * The origin of a remote page, from the URL the manifest or the user gave: `https://` anywhere, or
 * `http://` on this machine only, without credentials. Null when the value is not one.
 */
export function remotePageOrigin(value: string): string | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (url.username || url.password) return null;
  if (url.protocol === 'https:') return url.origin;
  if (url.protocol === 'http:' && loopbackHostname(url.hostname)) return url.origin;
  return null;
}

export const REMOTE_PAGE_PARTITION_PREFIX = 'persist:crowdex-ext-';

/**
 * The persistent browser session of an app's remote pages on Desktop: its own cookies and
 * storage, apart from the app, the Browser panel and other apps.
 */
export function remotePagePartition(plugin: string): string {
  return `${REMOTE_PAGE_PARTITION_PREFIX}${plugin}`;
}

/** Where a remote page opens, once resolved. */
export type RemotePageTarget =
  | {
      status: 'ready';
      url: string;
      origin: string;
      /** A one-time URL on `origin` that opens `url` signed in, when asked for and available. */
      signInUrl?: string;
      /**
       * A Crowlingo application (`service`): it lets Crowdex frame it and draws its sections and
       * bar through the embed bridge (`@crow/ui`); other pages open in a tab of their own on the web.
       */
      embeddable?: boolean;
    }
  /** The user has no default connection of `app` with a valid origin. */
  | { status: 'unconfigured'; app: string };
